import { Button, Card, Divider, Dropdown, List, Menu, notification, Space } from "antd";
import { useEffect, useState } from "react";
import { getCloudService } from "../../backend";
import { MoreOutlined } from "@ant-design/icons";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import AwsLogo from "../../assets/aws-logo.svg";
import ConnectAwsAccountModal from "../../components/settings/ConnectAwsAccountModal";
import { trackSpecCloudAccountInit } from "../../backend/tracking/user-settings";
import ExplanationButton from "../../components/explanations/ExplanationButton";
import CloudAccountExplanation from "../../components/explanations/CloudAccountExplanation";
import ChooseAwsAccountModal from "../../components/modals/ChooseAwsAccountModal";
import { linkAwsAccountToStage } from "../../utils/aws-accounts";
import { useParams } from "react-router-dom";

export default function ProjectAdminCloud() {
    const { projectId } = useParams();
    const [loadingAccounts, setLoadingAccounts] = useState(true);
    const [awsAccounts, setAwsAccounts] = useState<{ id: string; name: string; role: string; }[]>([]);
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const [connectAwsAccountModalVisible, setConnectAwsAccountModalVisible] = useState(false);
    const [chooseAwsAccountModal, setChooseAwsAccountModal] = useState(false);
    const [awsAccountId, setAwsAccountId] = useState<string>();

    useEffect(() => {
        loadAwsAccounts();
    }, []);

    async function loadAwsAccounts() {
        try {
            setLoadingAccounts(true);
            const { data: { awsAccounts } } = await getCloudService().getAwsAccounts(projectId!);

            setAwsAccounts(awsAccounts.map(acc => ({
                id: acc.id,
                name: acc.accountName,
                role: acc.iamRole
            })));
        } catch (error) {

        } finally {
            setLoadingAccounts(false);
        }
    }

    async function handleDisconnectAwsAccount(accountId: string) {
        try {
            await getCloudService().deleteAwsAccount(accountId, projectId!);
            await loadAwsAccounts();
            notification.success({
                message: "AWS account disconnected"
            });
        } catch (error: any) {
            notification.error({
                message: "Error disconnecting AWS account",
                description: error.response.data.message
            });
        }
    }

    function showDisconnectConfirm(accountId: string, confirmation: string) {
        openConfirmModal({
            title: "Disconnect AWS account?",
            description: <>
                <p>By disconnecting this AWS account, Microtica won't be able to perform any infrastructure changes on your account. However you can continue managing the infrastructure from the AWS console.</p>
                <p>Any deployments on environments which are using this AWS account will be disabled until the account is connected again.</p>
                <p>To reactivate the infrastructure management just reconnect this account.</p>
            </>,
            confirmation,
            okText: `Disconnect ${confirmation}`,
            cancelText: 'Cancel',
            onOk: () => {
                return handleDisconnectAwsAccount(accountId);
            }
        })
    }

    return (
        <>
            <Card>
                <Card.Meta
                    title={
                        <>
                            <span>
                                Connect Cloud Account
                            </span>
                            <ExplanationButton
                                content={<CloudAccountExplanation />}
                            />
                        </>
                    }
                />
                <Divider />
                <Space direction="vertical">
                    <Space>
                        <Button onClick={() => {
                            setConnectAwsAccountModalVisible(true);
                            trackSpecCloudAccountInit("aws");
                        }}>
                            <Space>
                                <img src={AwsLogo} style={{ width: "20px" }} alt="aws" /> Connect AWS Account
                            </Space>
                        </Button>
                    </Space>
                </Space>
            </Card>
            <br />

            <List
                bordered
                itemLayout="horizontal"
                className="ant-list-wrapper-bordered"
                dataSource={awsAccounts}
                loading={loadingAccounts}
                header="Connected AWS accounts"
                locale={{ emptyText: "No connected AWS accounts yet!" }}
                renderItem={item => (
                    <List.Item
                        style={{ background: "white" }}
                        actions={[
                            <Dropdown
                                placement="bottomRight"
                                trigger={["click"]}
                                overlay={
                                    <Menu>
                                        <Menu.Item key="review" danger onClick={() => showDisconnectConfirm(item.id, item.name)}>
                                            Disconnect {item.name}
                                        </Menu.Item>
                                    </Menu>
                                }>
                                <Button icon={<MoreOutlined />} />
                            </Dropdown>
                        ]}
                    >
                        <List.Item.Meta
                            avatar={<img src={AwsLogo} style={{ width: "24px" }} alt="aws" />}
                            title={`${item.name} (${item.id})`}
                            description={item.role}
                            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                        />
                    </List.Item>
                )}
            />
            {
                // Workaround. The modal is not unmouting when closed so we need to unmount the whole component using 'modalVisible'
                // We have a process of periodic API calls which remain active even if when the modal closes
                connectAwsAccountModalVisible &&
                <ConnectAwsAccountModal
                    visible={connectAwsAccountModalVisible}
                    onOk={(newAwsAccountId) => {
                        loadAwsAccounts();
                        setConnectAwsAccountModalVisible(false);
                        setAwsAccountId(newAwsAccountId);
                        setChooseAwsAccountModal(true);
                    }}
                    onCancel={() => setConnectAwsAccountModalVisible(false)}
                />
            }
            {
                // This modal becomes active when the user connects an AWS account and now needs to select a stage and a region
                chooseAwsAccountModal &&
                <ChooseAwsAccountModal
                    selectedAccount={{ accountId: awsAccountId, region: undefined }}
                    visible={chooseAwsAccountModal}
                    chooseStage={true}
                    onCancel={() => setChooseAwsAccountModal(false)}
                    onOk={async ({ accountId, region, envId }: { accountId: string, region: string, envId?: string }) => {
                        await linkAwsAccountToStage({
                            envId: envId!,
                            projectId: projectId!,
                            awsAccountId: accountId,
                            awsRegion: region
                        });
                        setChooseAwsAccountModal(false);
                    }}
                />
            }
        </>
    )
}